import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getUserById } from "../../services/operations/authAPI";
import { useTranslation } from "react-i18next";
import { getSchoolBySchoolID } from "../../services/operations/schoolAPI";
import { getStudentFees } from "../../services/operations/studentFeesAPI"; 
import { Link } from "react-router-dom";
import { CreditCard, Zap, Calendar, Activity } from "lucide-react";

export default function Profile() {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.profile);
  const dispatch = useDispatch();
  
  const [schoolName, setSchoolName] = useState("");
  const [linkedUsers, setLinkedUsers] = useState([]);
  const [feesData, setFeesData] = useState({});

  // 1. Fetch Linked Users (Parents or Children)
  useEffect(() => {
    const fetchLinkedUsers = async () => {
      let users = [];
      if (user.role === "parent" && user.children?.length > 0) {
        users = await Promise.all(
          user.children.map(async (id) => await dispatch(getUserById(id)))
        );
      } else if (user.parents?.length > 0) {
        users = await Promise.all(
          user.parents.map(async (id) => await dispatch(getUserById(id)))
        );
      }
      setLinkedUsers(users);
    };
    fetchLinkedUsers();
  }, [user, dispatch]);

  // 2. Fetch Fees for linked students (if any) or current user
  useEffect(() => {
    const fetchAllFees = async () => {
      const feesMap = {};

      if (user.role === "student") {
        const data = await getStudentFees(user._id);
        if (data?.list) feesMap[user._id] = data.list[0];
      }

      if (user.role === "parent" && linkedUsers.length > 0) {
        for (const child of linkedUsers) {
          const data = await getStudentFees(child._id);
          if (data?.list) feesMap[child._id] = data.list[0];
        }
      }
      setFeesData(feesMap);
    };

    if (linkedUsers.length > 0 || user.role === "student") {
      fetchAllFees();
    }
  }, [linkedUsers, user]);

  // 3. Fetch School Name
  useEffect(() => {
    const fetchSchool = async () => {
      if (!user?.schoolId) return;
      try {
        const response = await getSchoolBySchoolID(user.schoolId);
        if (response) setSchoolName(response.name);
      } catch (err) {
        console.error("FETCH SCHOOL ERROR:", err);
      }
    };
    fetchSchool();
  }, [user]);

  // --- SUBSCRIPTION DATA CALCULATIONS ---
  const currentSub = user?.subscription?.find(s => s.status === 'active') || user?.subscription?.[user?.subscription?.length - 1];
  
  const planName = currentSub?.plan || 'N/A';
  const status = currentSub?.status || 'N/A';
  const startDate = currentSub?.planActivatedDate;
  const endDate = currentSub?.planExpireDate;
  const maxAPIs = currentSub?.planDetails?.maxAPIsAllowed || 0;
  const apisUsed = currentSub?.planDetails?.apisUsed || 0;
  const isUnlimited = maxAPIs === -1;

  // Calculate remaining days
  let remainingDays = 0;
  if (endDate && status === "active") {
    const end = new Date(endDate);
    const now = new Date();
    if (end > now) {
      remainingDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    }
  }

  // Calculate Usage %
  const usagePercentage = isUnlimited || maxAPIs === 0 ? 0 : Math.min((apisUsed / maxAPIs) * 100, 100);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  if (!user) {
    return <div className="flex items-center justify-center h-full"><p>{t("profile.loading")}</p></div>;
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#00254D]">{t("profile.myProfile")}</h1>
      </div>

      {/* TOP SECTION: Basic Info (Left) & Plan Details (Right) */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
        
        {/* LEFT COLUMN: BASIC INFO (Takes up 1/3 of space on large screens) */}
        <div className="xl:col-span-1 bg-[#00254D] shadow-md rounded-lg p-6 text-white h-fit">
          <h2 className="text-xl font-semibold mb-4 border-b border-blue-400 pb-2">{t("profile.myProfile")}</h2>
          <div className="space-y-4">
            <div><label className="text-xs text-gray-400 uppercase">{t("profile.name")}</label><p className="font-medium">{user.name}</p></div>
            <div><label className="text-xs text-gray-400 uppercase">{t("profile.email")}</label><p className="font-medium">{user.email}</p></div>
            <div><label className="text-xs text-gray-400 uppercase">{t("profile.role")}</label><p className="font-medium">{user.role.toUpperCase()}</p></div>
            {/* {schoolName && <div><label className="text-xs text-gray-400 uppercase">{t("profile.schoolId")}</label><p className="font-medium">{schoolName}</p></div>} */}
            {user.studentId && <div><label className="text-xs text-gray-400 uppercase">{t("profile.studentId")}</label><p className="font-medium">{user.studentId}</p></div>}
          </div>
        </div>

        {/* RIGHT COLUMN: SUBSCRIPTION & USAGE (Takes up 2/3 of space on large screens) */}
        {currentSub && (
          <div className="xl:col-span-2 bg-[#0A0A0A] border border-white/10 rounded-xl p-6 shadow-xl text-white h-fit">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <CreditCard className="text-indigo-500" size={20} />
                Plan & Usage
              </h3>
              <Link
                to="/dashboard/student/upgrade-plan"
                className="text-sm font-semibold text-indigo-400 hover:text-indigo-300 flex items-center gap-2"
              >
                Upgrade
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Plan Info */}
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                <p className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-1">
                  Active Plan
                </p>
                <p className="text-xl font-bold text-white capitalize flex items-center gap-2">
                  <Zap className="text-yellow-500" size={18} fill="currentColor" />
                  {planName}
                </p>
                <p className={`text-xs font-medium mt-1 ${status === "expired" ? "text-red-500" : "text-emerald-500"}`}>
                  Status: <span className="capitalize">{status}</span>
                </p>
              </div>

              {/* Expiry Info */}
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5 flex flex-col justify-between">
                <div>
                  <p className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-1">
                    Time Remaining
                  </p>
                  <p className="text-xl font-bold text-white flex items-center gap-2">
                    <Calendar className="text-indigo-400" size={18} />
                    {status === "active" ? `${remainingDays} Days Left` : "Expired"}
                  </p>
                </div>

                <div className="mt-3 pt-3 border-t border-white/5 space-y-1">
                  <div className="flex justify-between text-[10px] font-medium">
                    <span className="text-neutral-500 uppercase tracking-tight">Started:</span>
                    <span className="text-neutral-200">{formatDate(startDate)}</span>
                  </div>
                  <div className="flex justify-between text-[10px] font-medium">
                    <span className="text-neutral-500 uppercase tracking-tight">Expires:</span>
                    <span className="text-neutral-200">{formatDate(endDate)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Usage Info & Progress */}
            <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
              <div className="w-full md:w-1/3 p-4 bg-white/5 rounded-2xl border border-white/5">
                <p className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-1">
                  API Usage
                </p>
                <div className="flex justify-between items-end">
                  <p className="text-2xl font-bold text-white flex items-center gap-2">
                    <Activity className="text-blue-500" size={20} />
                    {apisUsed} <span className="text-neutral-500 text-sm font-medium">/ {isUnlimited ? "∞" : maxAPIs}</span>
                  </p>
                </div>
                {!isUnlimited && (
                  <p className="text-xs text-neutral-400 mt-1">
                    {Math.max(maxAPIs - apisUsed, 0)} remaining
                  </p>
                )}
              </div>

              {/* Progress Bar */}
              {!isUnlimited && (
                <div className="w-full md:w-2/3 space-y-2">
                  <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-neutral-400">
                    <span>Usage Limit</span>
                    <span>{Math.round(usagePercentage)}% Used</span>
                  </div>
                  <div className="w-full bg-white/5 rounded-full h-2.5 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${
                        usagePercentage > 90
                          ? "bg-red-500"
                          : usagePercentage > 75
                            ? "bg-yellow-500"
                            : "bg-indigo-500"
                      }`}
                      style={{ width: `${usagePercentage}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* BOTTOM SECTION: LINKED USERS & FEES (Full Width) */}
      <div className="mt-8">
        <h2 className="text-xl font-bold text-[#00254D] mb-4 border-b pb-2">
          Family Linked & Fees
        </h2>
        <div className="space-y-6">
          {linkedUsers.length > 0 ? (
            linkedUsers.map((linkedUser) => (
              <div key={linkedUser._id} className="bg-white shadow-md rounded-lg border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 p-4 border-b flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-bold text-[#00254D]">
                      {linkedUser.role === "student" ? "Child:" : "Parent:"} {linkedUser.name}
                    </h3>
                    <p className="text-sm text-gray-600">{linkedUser.email}</p>
                  </div>
                  <span className="px-3 py-1 bg-[#00254D] text-white text-xs font-semibold rounded-full uppercase">
                    {linkedUser.role}
                  </span>
                </div>

                {/* SHOW FEES IF LINKED USER IS A STUDENT */}
                {linkedUser.role === "student" && feesData[linkedUser._id] ? (
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-700 mb-3 underline">Fees Summary ({feesData[linkedUser._id].academicYear})</h4>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                      <div className="bg-blue-50 p-3 rounded border border-blue-100">
                        <p className="text-xs text-blue-600 font-bold uppercase">Total</p>
                        <p className="text-lg font-bold text-gray-800">₹{feesData[linkedUser._id].payableAmount}</p>
                      </div>
                      <div className="bg-green-50 p-3 rounded border border-green-100">
                        <p className="text-xs text-green-600 font-bold uppercase">Paid</p>
                        <p className="text-lg font-bold text-gray-800">₹{feesData[linkedUser._id].paidAmount}</p>
                      </div>
                      <div className="bg-red-50 p-3 rounded border border-red-100">
                        <p className="text-xs text-red-600 font-bold uppercase">Pending</p>
                        <p className="text-lg font-bold text-gray-800">₹{feesData[linkedUser._id].pendingAmount}</p>
                      </div>
                    </div>

                    {/* Installments Table */}
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full text-sm text-left border">
                        <thead className="bg-gray-100 text-gray-700">
                          <tr>
                            <th className="p-3 border font-semibold">Installment</th>
                            <th className="p-3 border font-semibold">Due Date</th>
                            <th className="p-3 border font-semibold">Amount</th>
                            <th className="p-3 border font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {feesData[linkedUser._id].installments?.map((inst, i) => (
                            <tr key={i} className="border-b hover:bg-gray-50">
                              <td className="p-3 border text-gray-800">{inst.title || `Inst ${inst.installmentNo}`}</td>
                              <td className="p-3 border text-gray-600">{new Date(inst.dueDate).toLocaleDateString()}</td>
                              <td className="p-3 border font-medium">₹{inst.amount}</td>
                              <td className="p-3 border">
                                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                                  inst.status === "COMPLETED" ? "bg-green-100 text-green-700 border border-green-200" : 
                                  inst.status === "OVERDUE" ? "bg-red-100 text-red-700 border border-red-200" : "bg-yellow-100 text-yellow-700 border border-yellow-200"
                                }`}>
                                  {inst.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  linkedUser.role === "student" && <p className="p-4 text-gray-400 italic">No fee records found for this student.</p>
                )}
              </div>
            ))
          ) : (
            <div className="bg-gray-100 p-10 rounded-lg text-center text-gray-500 border border-gray-200">
              No linked family accounts found.
            </div>
          )}
        </div>
      </div>

    </div>
  );
}